import { cn } from 'utils/classNames';
import { escapeRegExp } from 'utils/helpers';

interface Props {
	/** Container class name. */
	className?: string;

	/** Part of the text to highlight. */
	highlight: string | undefined;

	/** Which part to visually mark. */
	markPart?: 'match' | 'rest';

	/** The full text. */
	text: string;
}

/** Render `text` while highlighting any matching `highlight` parts. */
export default function HighlightedText({
	className,
	highlight = '',
	markPart = 'rest',
	text,
}: Props) {
	if (!highlight.trim()) {
		return <span className={className}>{text}</span>;
	}
	const splitRegex = new RegExp(`(${escapeRegExp(highlight)})`, 'gi');
	const checkRegex = new RegExp(escapeRegExp(highlight), 'i');
	const parts = text.split(splitRegex).filter(Boolean);
	return (
		<span className={className}>
			{parts.map((part, i) =>
				checkRegex.test(part) ? (
					<mark
						// The text could contain repeating chunks so it can't be used as key.
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						className={cn(
							'bg-transparent text-inherit',
							markPart === 'match' && 'font-bold',
							markPart !== 'match' && 'font-normal',
						)}
					>
						{part}
					</mark>
				) : (
					<span
						// The text could contain repeating chunks so it can't be used as key.
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						className={cn(
							markPart === 'rest' && 'font-bold',
							markPart !== 'rest' && 'font-normal',
						)}
					>
						{part}
					</span>
				),
			)}
		</span>
	);
}
HighlightedText.displayName = 'HighlightedText';
